import { Component, Input } from '@angular/core';
import { RainCard } from '../../models/card.model';
import { CardService } from '../../services/card.service';
import { finalize } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-card-list',
  standalone: false,
  templateUrl: './card-list.component.html',
  styleUrl: './card-list.component.css',
})
export class CardListComponent {
  @Input() uuid!: string;
  @Input() balance!: number;
  @Input() rainStatus!: string;
  @Input() rainTermsAccepted!: boolean;
  @Input() hasPrivyWallet = false;

  cards: RainCard[] = [];
  loading = false;
  isComplianceAdmin = false;
  isMigratingCard = false;

  get canCreateCard(): boolean {
    if (!this.rainStatus) return false;
    const s = this.rainStatus.toUpperCase().replace(/[-_\s]/g, '');
    return ['NOTSTARTED', 'PENDING', 'APPROVED', 'ACTIVE'].includes(s);
  }

  get migrateCardDisabledReason(): string {
    if (!this.hasPrivyWallet) return 'User has no Privy wallet.';
    if (this.rainStatus?.toUpperCase() !== 'APPROVED') {
      return 'Rain user status must be approved.';
    }
    return '';
  }

  get canMigrateCard(): boolean {
    return !this.migrateCardDisabledReason;
  }

  constructor(
    private cardService: CardService,
    private authService: AuthService,
  ) {}

  ngOnInit() {
    this.loadCards(this.uuid);
    this.checkPrivileges();
  }

  checkPrivileges() {
    this.isComplianceAdmin = this.authService.isComplianceAdmin();
  }

  loadCards(uuid: string) {
    this.loading = true;

    this.cardService.getCards(uuid).subscribe((response) => {
      this.cards = response;
      this.loading = false;
    });
  }

  createCard() {
    if (confirm('Confirm create card?')) {
      this.cardService
        .createCard(this.uuid)
        .pipe(finalize(() => this.loadCards(this.uuid)))
        .subscribe({
          next: () => console.log('Card created successfully'),
          error: (err) => {
            console.error('Failed to create card:', err);
            let errorMsg = 'Failed to create card';
            if (err?.error?.detail) {
              errorMsg =
                typeof err.error.detail === 'string'
                  ? err.error.detail
                  : JSON.stringify(err.error.detail);
            } else if (err?.error?.message) {
              errorMsg =
                typeof err.error.message === 'string'
                  ? err.error.message
                  : JSON.stringify(err.error.message);
            } else if (err?.error?.error) {
              errorMsg =
                typeof err.error.error === 'string'
                  ? err.error.error
                  : JSON.stringify(err.error.error);
            } else if (typeof err?.error === 'string') {
              errorMsg = err.error;
            }
            alert(errorMsg);
          },
        });
    }
  }

  migrateCard() {
    if (
      !confirm(
        'Confirm card migration? This cancels the active card and deletes the Rain user.',
      )
    ) {
      return;
    }
    this.runCardMigration(false);
  }

  private runCardMigration(force: boolean) {
    this.isMigratingCard = true;
    this.cardService
      .migrateCard(this.uuid, force)
      .pipe(finalize(() => (this.isMigratingCard = false)))
      .subscribe({
        next: (res) => {
          alert(res?.detail ?? 'User card successfully migrated.');
          this.loadCards(this.uuid);
        },
        error: (err) => {
          const detail: string =
            typeof err?.error?.detail === 'string'
              ? err.error.detail
              : 'Card migration failed.';
          // Backend refuses when spending power is above the threshold unless forced.
          if (!force && detail.includes('set force to override')) {
            if (confirm(`${detail}\n\nForce the migration anyway?`)) {
              queueMicrotask(() => this.runCardMigration(true));
            }
            return;
          }
          alert(detail);
        },
      });
  }

  syncCard(card_uuid: string) {
    if (confirm('Confirm sync balance?')) {
      this.cardService
        .manageCard(this.uuid, card_uuid, 'synchronize')
        .pipe(finalize(() => this.loadCards(this.uuid)))
        .subscribe({
          next: () => console.log('Card synced successfully'),
          error: (err) => console.error('Failed to sync card:', err),
        });
    }
  }

  lockCard(card_uuid: string) {
    if (confirm('Confirm lock card?')) {
      this.cardService
        .manageCard(this.uuid, card_uuid, 'lock')
        .pipe(finalize(() => this.loadCards(this.uuid)))
        .subscribe({
          next: () => console.log('Card locked successfully'),
          error: (err) => console.error('Failed to lock card:', err),
        });
    }
  }

  unlockCard(card_uuid: string) {
    if (confirm('Confirm unlock card?')) {
      this.cardService
        .manageCard(this.uuid, card_uuid, 'unlock')
        .pipe(finalize(() => this.loadCards(this.uuid)))
        .subscribe({
          next: () => console.log('Card unlocked successfully'),
          error: (err) => console.error('Failed to unlock card:', err),
        });
    }
  }

  cancelCard(card_uuid: string) {
    if (confirm('Confirm cancel card?')) {
      this.cardService
        .manageCard(this.uuid, card_uuid, 'cancel')
        .pipe(finalize(() => this.loadCards(this.uuid)))
        .subscribe({
          next: () => console.log('Card cancelled successfully'),
          error: (err) => console.error('Failed to cancel card:', err),
        });
    }
  }

  updateLimit(card_uuid: string) {
    const input = prompt('Enter new card limit amount:');
    if (input !== null) {
      const amount = parseFloat(input);
      if (!isNaN(amount) && amount >= 0) {
        this.cardService
          .updateCardLimit(this.uuid, card_uuid, amount)
          .pipe(finalize(() => this.loadCards(this.uuid)))
          .subscribe({
            next: () => console.log('Card limit updated successfully'),
            error: (err) => console.error('Failed to update card limit:', err),
          });
      } else {
        alert('Please enter a valid positive number.');
      }
    }
  }
}
